// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AggregationEditorContext,
  AggregationsSubEditorContainer,
} from 'flow/editor/components/aggregations';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { createWorkflowHistoryThunk, resetSubEditor } from 'flow/store/redux/thunks';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import type { AggregationSourceData } from 'flow/editor/components/aggregations/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from 'flow/editor/components/aggregations/transformers';
import { getIsSaveDisabled } from 'flow/editor/components/aggregations/helpers';
import { INACTIVE_AGGREGATION_OBJECT_MESSAGE } from 'flow/editor/components/aggregations/constants';
import {
  selectRuleBuilderActiveSubEditor,
  selectWorkflowProps,
  selectWorkflowType,
} from 'flow/store/redux/selectors';
import { WORKFLOW_TYPES } from '~/constants';
import { RuleBuilderSubEditorSide } from 'flow/store/engine/types/utils';
import { getIsPreview } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { getMultiSelectProps } from './helpers';
import { updateThreshold as updateThresholdSlice } from 'flow/store/redux/slice';
import { keyBinds } from '../../constants';
import { datadogRum } from '@datadog/browser-rum';
import { useKeyboardFocus, useSnackBar } from '@usealloy/component-library';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowHistoryContext } from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { UserContext } from '~/dashboard/context';
import { createUiEffectContext } from 'flow/service/history';
import { isPreviewAction } from 'flow/service/actions';

const AggregationsWrapper = () => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const { editMode, keyboardFocus, sourceAction } = useContext(RuleBuilderContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { aggregationConfig: aggregationConfigByObjectType } = useAppSelector(selectWorkflowProps);
  const addSnackBar = useSnackBar();
  const isPortfolioWorkflow = useAppSelector(selectWorkflowType) === WORKFLOW_TYPES.PORTFOLIO;

  const side = activeSubEditor.side === RuleBuilderSubEditorSide.LHS ? 'left' : 'right';
  const attribute = populatedThreshold[side]?.attribute;

  const aggregationConfig = useMemo(
    () => getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType),
    [attribute, aggregationConfigByObjectType],
  );

  const multiSelectProps = useMemo(() => getMultiSelectProps({
    action: populatedThreshold[side].action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  }), [populatedThreshold, side, editMode, activeSubEditor.thresholdId]);

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);

  const { aggregationComponents } = populatedThreshold[side];

  const [aggregationContextSourceData, setAggregationContextSourceData] = useState<
  AggregationSourceData
  >(() => aggregationComponentsToSourceData(
    aggregationComponents, aggregationConfig?.objectType,
  ));

  useEffect(() => {
    // hydrates the sub editor if something else outside the subeditor
    // changes the data route, like undo/redo
    setAggregationContextSourceData(
      aggregationComponentsToSourceData(
        aggregationComponents, aggregationConfig?.objectType,
      ),
    );
  }, [
    aggregationComponents,
    aggregationConfig?.objectType,
  ]);

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(aggregationContextSourceData),
    [aggregationContextSourceData],
  );

  const aggregationKeyboardFocus = useKeyboardFocus({
    containerRef: aggregationEditorContainerRef,
    keyBinds,
    onError: (error, context) => datadogRum.addError(error, context),
  });

  const handleClose = useCallback(() => {
    dispatch(resetSubEditor());
    keyboardFocus.enable();
  }, [dispatch, keyboardFocus]);

  const workflowHistoryContext: WorkflowHistoryContext = useMemo(() => ({
    operation: Operation.Modify,
    uiEffectContext: createUiEffectContext(changeDescription.aggregation, {
      actionId: populatedThreshold.actionId,
      subEditorArgs: {
        threshold: {
          publicId: populatedThreshold.publicId,
          side: activeSubEditor.side as RuleBuilderSubEditorSide,
        },
      },
    }),
  }), [populatedThreshold, activeSubEditor.side]);

  const handleSave = useCallback(() => {
    // objectSlug/objectVersionPublicVersion may be absent when aggregationConfig
    // wasn't loaded yet at threshold-creation time. Fall back to the current config,
    // but let any already-persisted values take precedence via the spread.
    const withFallbacks = [{
      objectSlug: aggregationConfig?.objectType,
      objectVersionPublicVersion: aggregationConfig?.publicVersion,
      ...aggregationComponents[0],
    }, ...aggregationComponents.slice(1)];

    dispatch(
      createWorkflowHistoryThunk(
        () => dispatch(updateThresholdSlice({
          path: `${populatedThreshold.publicId}.${side}.aggregationComponents`,
          value: sourceDataToAggregationComponents(
            aggregationContextSourceData,
            withFallbacks,
          ),
        })),
        Boolean(featureFlags?.workflowEditorUndoRedo) && !isPreviewAction(sourceAction),
        workflowHistoryContext,
      ),
    );

    handleClose();
  }, [
    dispatch,
    workflowHistoryContext,
    handleClose,
    aggregationComponents,
    aggregationContextSourceData,
    aggregationConfig,
    populatedThreshold,
    side,
    featureFlags,
    sourceAction,
  ]);

  const aggregationEditorContext = useMemo(() => ({
    sourceData: aggregationContextSourceData,
    setSourceData: setAggregationContextSourceData,
    handleSave,
    handleCancel: () => {
      setAggregationContextSourceData(aggregationComponentsToSourceData(
        aggregationComponents, aggregationConfig?.objectType,
      ));
      handleClose();
    },
    aggregationEditorContainerRef,
    multiSelectProps,
    aggregationConfig,
    isSaveDisabled,
    validationErrors: {},
    keyboardFocus: aggregationKeyboardFocus,
    isPortfolioWorkflow,
  }), [
    aggregationContextSourceData,
    aggregationComponents,
    multiSelectProps,
    aggregationConfig,
    isSaveDisabled,
    aggregationKeyboardFocus,
    handleClose,
    handleSave,
    isPortfolioWorkflow,
  ]);

  // The object's active version isn't selectable (e.g. an existing rule on an
  // object that has since been deactivated). Close the editor and explain why
  // rather than rendering a broken/blank editor.
  useEffect(() => {
    if (!aggregationConfig) {
      addSnackBar({ title: INACTIVE_AGGREGATION_OBJECT_MESSAGE, type: 'danger' });
      handleClose();
    }
  }, [aggregationConfig, addSnackBar, handleClose]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationsWrapper;
